import { useEffect, useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Download, RefreshCw, X, Share, Plus, Smartphone } from "lucide-react";

// ---------- helpers ----------
function isMobile() {
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
}
function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}
function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}
const STORAGE_KEY = "bauen_pwa_dismissed_at";
const REMIND_AFTER_MS = 5 * 24 * 60 * 60 * 1000; // volta a lembrar após 5 dias

/**
 * Convite proativo para instalação do Portal BAUEN no dispositivo.
 * - Android/Chrome/Edge: usa o `beforeinstallprompt` nativo.
 * - iOS Safari: exibe passo-a-passo com o ícone de compartilhar → "Adicionar à Tela Inicial".
 * - Se o app já foi instalado (standalone), não aparece.
 * - Se o usuário dispensou, só volta a aparecer após 5 dias.
 */
export function PwaPrompt() {
  const [nativePrompt, setNativePrompt] = useState(null);
  const [updateReady, setUpdateReady] = useState(false);
  const [pendingReg, setPendingReg] = useState(null);
  const [show, setShow] = useState(false);
  const [showIosGuide, setShowIosGuide] = useState(false);

  const shouldShowByDefault = useMemo(() => {
    if (isStandalone()) return false;
    if (!isMobile()) return false;
    const dismissedAt = Number(localStorage.getItem(STORAGE_KEY) || 0);
    if (dismissedAt && Date.now() - dismissedAt < REMIND_AFTER_MS) return false;
    return true;
  }, []);

  useEffect(() => {
    const onInstall = (e) => {
      setNativePrompt(e?.detail || window.__bauenInstallPrompt || null);
      if (shouldShowByDefault) setShow(true);
    };
    const onInstalled = () => {
      setNativePrompt(null);
      setShow(false);
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    };
    const onUpdated = (e) => { setUpdateReady(true); setPendingReg(e.detail?.registration || null); };

    window.addEventListener("bauen:install-available", onInstall);
    window.addEventListener("bauen:installed", onInstalled);
    window.addEventListener("bauen:sw-updated", onUpdated);

    // Se já havia um prompt guardado antes do mount
    if (window.__bauenInstallPrompt && shouldShowByDefault) {
      setNativePrompt(window.__bauenInstallPrompt);
      setShow(true);
    }

    // iOS Safari não emite o evento — abre convite manual após 1.2s
    if (shouldShowByDefault && isIOS()) {
      const t = setTimeout(() => setShow(true), 1200);
      return () => {
        clearTimeout(t);
        window.removeEventListener("bauen:install-available", onInstall);
        window.removeEventListener("bauen:installed", onInstalled);
        window.removeEventListener("bauen:sw-updated", onUpdated);
      };
    }

    // Chrome/Android: se o evento não chegar em 3s (ex.: já elegível mas sem promoção),
    // ainda assim mostramos o convite manual — o botão "Instalar" fica desabilitado
    // caso o browser não permita, mas a comunicação da possibilidade fica clara.
    let fallbackTimer;
    if (shouldShowByDefault) {
      fallbackTimer = setTimeout(() => setShow((s) => s || true), 3000);
    }
    return () => {
      if (fallbackTimer) clearTimeout(fallbackTimer);
      window.removeEventListener("bauen:install-available", onInstall);
      window.removeEventListener("bauen:installed", onInstalled);
      window.removeEventListener("bauen:sw-updated", onUpdated);
    };
  }, [shouldShowByDefault]);

  const install = async () => {
    if (isIOS()) { setShowIosGuide(true); return; }
    const prompt = nativePrompt || window.__bauenInstallPrompt;
    if (!prompt) { setShowIosGuide(true); return; }
    prompt.prompt();
    const choice = await prompt.userChoice.catch(() => ({ outcome: "dismissed" }));
    if (choice.outcome === "accepted") {
      setShow(false);
    }
    window.__bauenInstallPrompt = null;
    setNativePrompt(null);
  };

  const applyUpdate = () => {
    if (!pendingReg?.waiting) { window.location.reload(); return; }
    pendingReg.waiting.postMessage("SKIP_WAITING");
    navigator.serviceWorker.addEventListener("controllerchange", () => window.location.reload(), { once: true });
  };

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
    setShow(false);
    setShowIosGuide(false);
  };

  // Guia iOS
  if (showIosGuide) {
    return (
      <div
        role="dialog"
        aria-label="Instalar aplicativo no iPhone"
        data-testid="pwa-ios-guide"
        className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fade-in-up"
        onClick={dismiss}
      >
        <div
          className="w-full max-w-md bg-card border rounded-2xl shadow-xl p-6"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <Smartphone size={20} />
            </div>
            <div className="flex-1">
              <h3 className="font-heading font-semibold text-lg">Instalar no iPhone</h3>
              <p className="text-sm text-muted-foreground mt-0.5">Em 3 passos rápidos pelo Safari.</p>
            </div>
            <Button variant="ghost" size="icon" onClick={dismiss} aria-label="Fechar" data-testid="pwa-ios-close">
              <X size={16} />
            </Button>
          </div>

          <ol className="mt-5 space-y-3 text-sm">
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-primary/10 text-primary font-semibold text-xs flex items-center justify-center shrink-0">1</span>
              <span>Toque no ícone <b className="inline-flex items-center gap-1"><Share size={13} className="inline"/> Compartilhar</b> na barra inferior do Safari.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-primary/10 text-primary font-semibold text-xs flex items-center justify-center shrink-0">2</span>
              <span>Role para baixo e toque em <b className="inline-flex items-center gap-1"><Plus size={13} className="inline"/> Adicionar à Tela de Início</b>.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-primary/10 text-primary font-semibold text-xs flex items-center justify-center shrink-0">3</span>
              <span>Confirme tocando em <b>Adicionar</b>. Pronto — o Portal BAUEN estará na sua tela inicial.</span>
            </li>
          </ol>

          <Button className="mt-6 w-full" onClick={dismiss} data-testid="pwa-ios-done">Entendi</Button>
        </div>
      </div>
    );
  }

  const showUpdate = updateReady;
  if (!show && !showUpdate) return null;

  return (
    <div
      role="dialog"
      aria-label={showUpdate ? "Atualização disponível" : "Instalar aplicativo"}
      data-testid="pwa-prompt"
      className="fixed z-[60] bg-card border rounded-xl shadow-lg p-3 sm:p-4 flex items-center gap-3 animate-fade-in-up bottom-4 inset-x-4 sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 sm:max-w-md sm:w-[calc(100%-2rem)]"
      style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
    >
      <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
        {showUpdate ? <RefreshCw size={18} /> : <Smartphone size={18} />}
      </div>
      <div className="flex-1 min-w-0">
        {showUpdate ? (
          <>
            <p className="text-sm font-medium">Nova versão disponível</p>
            <p className="text-xs text-muted-foreground">Recarregue para aplicar as atualizações.</p>
          </>
        ) : (
          <>
            <p className="text-sm font-medium leading-tight">Instale o Portal BAUEN</p>
            <p className="text-xs text-muted-foreground leading-tight mt-0.5">Acesso rápido pela tela inicial.</p>
          </>
        )}
      </div>
      <Button size="sm" onClick={showUpdate ? applyUpdate : install} data-testid="pwa-action-btn" className="gap-1.5 shrink-0">
        {showUpdate ? "Atualizar" : (<><Download size={14} /> Instalar</>)}
      </Button>
      {!showUpdate && (
        <Button size="icon" variant="ghost" onClick={dismiss} data-testid="pwa-dismiss-btn" aria-label="Dispensar" className="shrink-0 h-8 w-8">
          <X size={15} />
        </Button>
      )}
    </div>
  );
}
